import React, { useState, useEffect } from "react";
import {
	formatTime,
	formatCurrency,
	formatStringToDate
} from "@/utils/helpers";

import { motion } from "framer-motion";
import {
	Users,
	DollarSign,
	Calendar,
	MessageSquare,
	TrendingUp,
	AlertTriangle,
	Shield,
	FileText,
	UserCheck,
	Baby,
	CheckCircle,
	ArrowRight,
	Eye,
	Link,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { GOOGLE_OAUTH_REDIRECT_URI } from "@/utils/const";

interface DashboardStats {
	totalParents: number;
	totalDoulas: number;
	activeDoulas: number;
	pendingApplications: number;
	totalRevenue: number;
	monthlyRevenue: number;
	totalAppointments: number;
	totalConversations: number;
	totalTransactions: number;
	platformCommission: number;
}

interface RecentApplication {
	id: string;
	user_id: string;
	status: string;
	created_at: string;
	users: {
		full_name: string;
		email: string;
		avatar_url?: string;
	};
	years_experience: number;
	location_city: string;
	location_state: string;
}

interface FlaggedConversation {
	id: string;
	parent_user: {
		id: string;
		full_name: string;
		avatar_url?: string;
		email: string;
	};
	doula_user: {
		id: string;
		full_name: string;
		avatar_url?: string;
		email: string;
	};
	flagged_message: {
		id: string;
		content: string;
		created_at: string;
		sender_type: "parent" | "doula";
	};
	flagged_count: number;
	created_at: string;
}

interface AdminUser {
	id: string;
	auth_user_id: string;
	email: string;
	full_name: string;
	role: string;
	google_access_token?: string;
	google_refresh_token?: string;
	google_token_expires_at?: string;
	google_calendar_name?: string;
}

import styles from "./Admin.module.css";

const AdminDashboard: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [loading, setLoading] = useState(true);
	const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
	const [stats, setStats] = useState<DashboardStats>({
		totalParents: 0,
		totalDoulas: 0,
		activeDoulas: 0,
		pendingApplications: 0,
		totalRevenue: 0,
		monthlyRevenue: 0,
		totalAppointments: 0,
		totalConversations: 0,
		totalTransactions: 0,
		platformCommission: 0,
	});
	const [recentApplications, setRecentApplications] = useState<
		RecentApplication[]
	>([]);
	const [flaggedConversations, setFlaggedConversations] = useState<
		FlaggedConversation[]
	>([]);

	useEffect(() => {
		if (user) {
			fetchDashboardData();
			loadAdminUser();
		}
	}, [user]);

	// Listen for OAuth messages from popup
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;

			if (event.data.type === "GOOGLE_OAUTH_SUCCESS") {
				loadAdminUser(); // Reload to show updated connection status
			} else if (event.data.type === "GOOGLE_OAUTH_ERROR") {
				alert(`Google Calendar connection failed: ${event.data.error}`);
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, []);

	const fetchDashboardData = async () => {
		try {
			setLoading(true);

			if (!user) {
				throw new Error("User not authenticated");
			}

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/get-admin-dashboard-stats`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch dashboard data");
			}

			const {
				stats: dashboardStats,
				recentApplications: applications,
				flaggedConversations: flagged,
			} = result.data;

			setStats(dashboardStats);
			setRecentApplications(applications);
			setFlaggedConversations(flagged);
		} catch (error) {
			// Set empty states on error
			setStats({
				totalParents: 0,
				totalDoulas: 0,
				activeDoulas: 0,
				pendingApplications: 0,
				totalRevenue: 0,
				monthlyRevenue: 0,
				totalAppointments: 0,
				totalConversations: 0,
				totalTransactions: 0,
				platformCommission: 0,
			});
			setRecentApplications([]);
			setFlaggedConversations([]);
		} finally {
			setLoading(false);
		}
	};

	const loadAdminUser = async () => {
		try {
			const { data, error } = await supabase
				.from("users")
				.select("*")
				.eq("auth_user_id", user?.id)
				.eq("role", "admin")
				.single();

			if (data) {
				setAdminUser(data);
			}
		} catch (error) {
			// Handle error silently
		}
	};

	const handleGoogleCalendarConnect = async () => {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-oauth-initiate`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						action: "initiate",
						redirect_uri: GOOGLE_OAUTH_REDIRECT_URI,
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to initiate Google OAuth");
			}

			// Open popup window for OAuth
			const popup = window.open(
				result.data.auth_url,
				"google-oauth",
				"width=500,height=600,scrollbars=yes,resizable=yes",
			);

			if (!popup) {
				alert(
					"Popup was blocked. Please allow popups for this site and try again.",
				);
				return;
			}

			// Set a timeout to reload data after OAuth completes
			setTimeout(() => {
				loadAdminUser();
			}, 10000); // 10 second timeout
		} catch (error) {
			alert("Failed to connect Google Calendar. Please try again.");
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "pending":
				return (
					<Badge
						variant="secondary"
						className="bg-newTheme-cotton text-newTheme-grey"
					>
						<AlertTriangle className="w-3 h-3 mr-1" />
						Pending
					</Badge>
				);
			case "active":
				return (
					<Badge
						variant="default"
						className="bg-newTheme-lightBlue text-newTheme-grey"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Active
					</Badge>
				);
			case "completed":
				return (
					<Badge
						variant="default"
						className="bg-newTheme-lime text-newTheme-grey"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Completed
					</Badge>
				);
			default:
				return (
					<Badge
						variant="secondary"
						className="bg-newTheme-cotton text-newTheme-grey"
					>
						{status}
					</Badge>
				);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-black font-karla">Loading admin dashboard...</p>
				</div>
			</div>
		);
	}

	return (
		<section className="adminDashboard py-8">
			{/* Stats Grid */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, delay: 0.1 }}
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
			>
				{/* Total Users */}
				<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<div className="w-12 h-12 bg-newTheme-lightBlue rounded-lg flex items-center justify-center">
								<Users className="h-6 w-6 text-newTheme-spruce" />
							</div>
							<div>
								<p className="text-sm text-newTheme-grey font-karla">
									Total Users
								</p>
								<p className="text-2xl font-bold font-karla text-black">
									{stats.totalParents + stats.totalDoulas}
								</p>
								<small className="block text-xs text-newTheme-grey font-karla leading-tight">
									{stats.totalParents} parents <span aria-hidden="true">•</span> {stats.totalDoulas} doulas
								</small>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Pending Applications */}
				<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<div className="w-12 h-12 bg-newTheme-orange rounded-lg flex items-center justify-center">
								<AlertTriangle className="h-6 w-6 text-white" />
							</div>
							<div className="flex-1">
								<p className="text-sm text-newTheme-grey font-karla">
									Pending Review
								</p>
								<p className="text-2xl font-bold text-black">
									{stats.pendingApplications}
								</p>
								<small className="block text-newTheme-grey font-karla leading-tight">
									Doula applications awaiting approval
								</small>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Monthly Revenue */}
				<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<div className="w-12 h-12 bg-newTheme-lime rounded-lg flex items-center justify-center">
								<DollarSign className="h-6 w-6 text-newTheme-spruce" />
							</div>
							<div>
								<p className="text-sm text-newTheme-grey font-karla">
									Monthly Revenue
								</p>
								<p className="text-2xl font-bold text-black font-karla">
									{formatCurrency(stats.monthlyRevenue)}
								</p>
								<small className="block text-xs text-newTheme-grey font-karla leading-tight">
									This month's revenue
								</small>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Active Doulas */}
				<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<div className="w-12 h-12 bg-newTheme-cotton rounded-lg flex items-center justify-center">
								<Shield className="h-6 w-6 text-newTheme-orange" />
							</div>
							<div className="flex-1">
								<p className="text-sm text-newTheme-grey font-karla">
									Active Doulas
								</p>
								<p className="text-2xl font-bold text-black font-karla">
									{stats.activeDoulas}
								</p>
								<small className="block text-xs text-newTheme-grey font-karla leading-tight">
									Verified and active listings
								</small>
							</div>
						</div>
					</CardContent>
				</Card>
			</motion.div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				{/* Main Content */}
				<div className="lg:col-span-2 space-y-6">
					{/* Pending Applications */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
					>
						<Card className="glass-card border-0 shadow-lg">
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="text-xl font-karla flex items-center">
										<UserCheck className="h-5 w-5 mr-2 text-newTheme-orange" />
										Pending Doula Applications
									</CardTitle>
									<Button
										onClick={() => navigate("/admin-applications")}
										variant="outline"
										size="sm"
										className={`${styles.primaryBtn} border border-newTheme-lightGrey`}
									>
										View All
										<ArrowRight className="h-4 w-4 ml-1" />
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{recentApplications.length > 0 ? (
									<div className="space-y-4">
										{recentApplications.map((application, index) => (
											<motion.div
												key={application.id}
												initial={{ opacity: 0, x: -20 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{
													duration: 0.6,
													delay: 0.3 + index * 0.1,
												}}
											>
												<Card className="bg-white/80 border border-gray-200 hover:shadow-md transition-all duration-200">
													<CardContent className="p-4">
														<div className="flex items-center space-x-4">
															<div className="w-12 h-12 rounded-lg overflow-hidden bg-newTheme-spruce flex items-center justify-center">
																{application.users.avatar_url ? (
																	<img
																		src={application.users.avatar_url}
																		alt={application.users.full_name}
																		className="w-full h-full object-cover"
																	/>
																) : (
																	<Users className="h-6 w-6 text-newTheme-lime" />
																)}
															</div>
															<div className="flex-1">
																<h4 className="font-semibold text-black font-karla">
																	{application.users.full_name}
																</h4>
																<p className="text-sm text-black font-karla">
																	{application.years_experience > 1 ? `${application.years_experience} years ` : `${application.years_experience} year `}
																	experience <span aria-hidden="true">•</span> {application.location_city},{" "}
																	{application.location_state}
																</p>
																<p className="text-xs text-newTheme-grey font-karla">
																	Applied {formatStringToDate(application.created_at)}
																</p>
															</div>
															<div className="flex items-center space-x-2">
																{getStatusBadge(application.status)}
																<Button
																	onClick={() =>
																		navigate(
																			`/admin-applications/${application.id}`,
																		)
																	}
																	variant="default"
																	size="sm"
																	className="border-0"
																>
																	<Eye className="h-3 w-3 mr-1" />
																	Review
																</Button>
															</div>
														</div>
													</CardContent>
												</Card>
											</motion.div>
										))}
									</div>
								) : (
									<div className="text-center py-8">
										<UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
										<p className="text-gray-600 font-karla">
											No pending applications
										</p>
										<p className="text-sm text-gray-500 font-karla mt-2">
											All doula applications have been reviewed
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</motion.div>

					{/* Flagged Messages/Conversations */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
					>
						<Card className="glass-card border-0 shadow-lg">
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="text-xl font-karla flex items-center">
										<AlertTriangle className="h-5 w-5 mr-2 text-newTheme-orange" />
										Flagged Messages
									</CardTitle>
									<Button
										onClick={() => navigate("/admin-messaging")}
										variant="outline"
										size="sm"
										className={`${styles.primaryBtn} border border-newTheme-lightGrey`}
									>
										View All
										<ArrowRight className="h-4 w-4 ml-1" />
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{flaggedConversations.length > 0 ? (
									<div className="space-y-4">
										{flaggedConversations
											.slice(0, 3)
											.map((conversation, index) => (
												<motion.div
													key={conversation.id}
													initial={{ opacity: 0, x: -20 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{
														duration: 0.6,
														delay: 0.5 + index * 0.1,
													}}
													className="p-4 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition-colors cursor-pointer"
													onClick={() => navigate("/admin-messaging")}
												>
													<div className="space-y-3">
														{/* Users */}
														<div className="flex items-center justify-between">
															<div className="flex items-center space-x-2">
																<Baby className="h-4 w-4 text-blue-600" />
																<span className="font-semibold text-gray-900 font-karla text-sm">
																	{conversation.parent_user.full_name}
																</span>
															</div>
															<div className="flex items-center space-x-2">
																<Shield className="h-4 w-4 text-purple-600" />
																<span className="font-semibold text-gray-900 font-karla text-sm">
																	{conversation.doula_user.full_name}
																</span>
															</div>
														</div>

														{/* Flagged Message */}
														<div className="bg-white p-3 rounded border border-red-300">
															<p className="text-sm text-gray-700 font-karla line-clamp-2">
																"{conversation.flagged_message.content}"
															</p>
															<div className="flex items-center justify-between mt-2">
																<span className="text-xs text-gray-500 font-karla">
																	From{" "}
																	{conversation.flagged_message.sender_type} •{" "}
																	{formatTime(
																		conversation.flagged_message.created_at,
																	)}
																</span>
																<Badge
																	variant="destructive"
																	className="text-xs"
																>
																	{conversation.flagged_count} flagged
																</Badge>
															</div>
														</div>
													</div>
												</motion.div>
											))}
									</div>
								) : (
									<div className="text-center py-8">
										<CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
										<p className="text-black font-karla">
											No flagged messages
										</p>
										<p className="text-sm text-newTheme-grey font-karla mt-2">
											All conversations are clean and safe
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</motion.div>
				</div>

				{/* Sidebar */}
				<aside className="space-y-6">
					{/* Quick Actions */}
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.3 }}
					>
						<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
							<CardHeader>
								<CardTitle className="text-lg font-karla">
									Quick Actions
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<Button
									onClick={() => navigate("/admin-applications")}
									className="w-full justify-start bg-white/80 hover:bg-white text-gray-900 border border-primary-200"
								>
									<UserCheck className="h-4 w-4 mr-3" />
									Review Applications
								</Button>
								<Button
									onClick={() => navigate("/admin-users")}
									className="w-full justify-start bg-white/80 hover:bg-white text-gray-900 border border-primary-200"
								>
									<Users className="h-4 w-4 mr-3" />
									Manage Users
								</Button>
								<Button
									onClick={() => navigate("/admin-messaging")}
									className="w-full justify-start bg-white/80 hover:bg-white text-gray-900 border border-primary-200"
								>
									<MessageSquare className="h-4 w-4 mr-3" />
									Monitor Messages
								</Button>
								<Button
									onClick={() => navigate("/admin-faq")}
									className="w-full justify-start bg-white/80 hover:bg-white text-gray-900 border border-primary-200"
								>
									<FileText className="h-4 w-4 mr-3" />
									Update FAQs
								</Button>
							</CardContent>
						</Card>
					</motion.div>

					{/* Platform Stats */}
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.4 }}
					>
						<Card className="glass-card border-0 shadow-lg">
							<CardHeader>
								<CardTitle className="text-lg font-karla">
									Platform Overview
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-newTheme-lime rounded-lg flex items-center justify-center">
												<DollarSign className="h-5 w-5 text-newTheme-spruce" />
											</div>
											<div>
												<p className="text-sm text-black font-karla">
													Total Revenue
												</p>
												<p className="text-lg font-semibold text-black">
													{formatCurrency(stats.totalRevenue)}
												</p>
											</div>
										</div>
									</div>

									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-newTheme-sky rounded-lg flex items-center justify-center">
												<Calendar className="h-5 w-5 text-newTheme-grey" />
											</div>
											<div>
												<p className="text-sm text-black font-karla">
													Total Appointments
												</p>
												<p className="text-lg font-semibold text-black">
													{stats.totalAppointments}
												</p>
											</div>
										</div>
									</div>

									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-newTheme-orange rounded-lg flex items-center justify-center">
												<MessageSquare className="h-5 w-5 text-newTheme-white" />
											</div>
											<div>
												<p className="text-sm text-black font-karla">
													Total Conversations
												</p>
												<p className="text-lg font-semibold text-black">
													{stats.totalConversations}
												</p>
											</div>
										</div>
									</div>

									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-newTheme-spruce rounded-lg flex items-center justify-center">
												<TrendingUp className="h-5 w-5 text-newTheme-white" />
											</div>
											<div>
												<p className="text-sm text-black font-karla">
													Platform Commission
												</p>
												<p className="text-lg font-semibold text-black">
													{formatCurrency(stats.platformCommission)}
												</p>
											</div>
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Google Calendar Integration */}
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.5 }}
					>
						<Card className="glass-card border-0 shadow-lg">
							<CardHeader>
								<CardTitle className="text-lg font-karla flex items-center">
									<Link className="h-5 w-5 mr-2 text-newTheme-lime" />
									Google Calendar
								</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-sm text-black font-karla mb-4">
									Connect your admin account to Google Calendar for automatic
									appointment reminders.
								</p>

								{/* Google Calendar Connection Card */}
								<div className="flex items-center justify-between p-4 bg-white/80 rounded-lg border border-gray-200">
									<div className="flex items-center space-x-3">
										<div>
											<h4 className="font-semibold text-black font-karla">
												Google Calendar
											</h4>
											<p className="text-sm text-newTheme-grey font-karla">
												{adminUser?.google_access_token
													? "Connected"
													: "Not connected"}
											</p>
											{adminUser?.google_calendar_name && (
												<p className="text-xs text-newTheme-grey font-karla">
													Calendar: {adminUser.google_calendar_name}
												</p>
											)}
										</div>
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleGoogleCalendarConnect()}
										className={`${styles.secondaryBtn} border border-newTheme-lightGrey`}
									>
										{adminUser?.google_access_token ? (
											<>
												<CheckCircle className="h-4 w-4 mr-2" />
												Connected
											</>
										) : (
											<>
												<Link className="h-4 w-4 mr-2" />
												Connect
											</>
										)}
									</Button>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</aside>
			</div>
		</section>
	);
};

export default AdminDashboard;
